import { get } from "svelte/store";
import noFlower from "/public/img/flowerNo.webp";
import noInventory from "/public/img/imageNo.png";
import { formatCurrency, price } from "@/assets/js/util";
import { getPublicViewByTenant, imgUrl } from "@/assets/js/pocketbase";
import commerce from "@/store/commerce.js";
import { ageOk, isRestrictedCatalog } from "@/assets/js/ageGate.js";

export function mapUnifiedCatalogItems(strains = [], inventory = [], scores = {}, canViewRestricted = false) {
    const flower = strains.map((s) => ({
        id: s.id,
        itemType: "flower",
        title: s.strain,
        subtitle: s.type || "",
        priceDisplay: price(s.customerPrice, 1, s.discount),
        originalPriceDisplay: s.discount ? formatCurrency(s.customerPrice) : "",
        image: imgUrl(s, noFlower, true),
        created: s.created,
        salesScore: scores[s.id] || 0,
        _raw: s,
    }));

    const inv = inventory.map((p) => {
        // Age-restricted (snus/nicotine): flagged here; the VIEW decides whether to
        // blur the image / withhold the price based on the live $ageOk store, so it
        // reacts to login/logout/verification and isn't baked into the cached data.
        const restricted = isRestrictedCatalog(p.catalog);
        return {
            id: p.id,
            itemType: "inventory",
            title: p.name,
            subtitle: p.catalog || "",
            priceDisplay: formatCurrency(p.price || 0),
            originalPriceDisplay: "",
            image: imgUrl(p, noInventory, true),
            created: p.created,
            stock: p.stock ?? 0,
            salesScore: scores[p.id] || 0,
            ageRestricted: restricted,
            _raw: p,
        };
    });

    const allItems = [...flower, ...inv];
    return allItems.sort((a, b) => {
        const aStock = a.stock ?? (a.itemType === "flower" ? 1 : 0);
        const bStock = b.stock ?? (b.itemType === "flower" ? 1 : 0);
        const aInStock = aStock >= 1;
        const bInStock = bStock >= 1;

        if (aInStock !== bInStock) {
            return aInStock ? -1 : 1;
        }

        return new Date(b.created) - new Date(a.created);
    });
}

export async function bootstrapStorefrontCatalog() {
    const [strains, inventory, bestsellers] = await Promise.all([
        getPublicViewByTenant("viewStrains"),
        getPublicViewByTenant("viewInventory"),
        getPublicViewByTenant("bestsellers").catch(() => []),
    ]);
    const flowerRaw = Array.isArray(strains) ? strains : [];
    const inventoryRaw = Array.isArray(inventory) ? inventory : [];
    const scores = {};
    if (Array.isArray(bestsellers)) {
        for (const b of bestsellers) {
            if (b && b.item) scores[b.item] = b.score || 0;
        }
    }
    const unified = mapUnifiedCatalogItems(flowerRaw, inventoryRaw, scores, get(ageOk));
    commerce.commit("inventoryItems", inventoryRaw);
    commerce.commit("items", unified);
    return unified;
}
